/**
 * Tenant branding ("Mandanten-Branding"): a clinic's look stored on its
 * Mandant in the backend instead of on a domain of its own. The link carries
 * the Mandanten-ID as `?m=<id>`; we keep the ID in localStorage, drop it from
 * the URL and fetch the brand from the public endpoint. After a login the
 * account's brand (the `branding` key of the login response) replaces it.
 *
 * Domain brands (training.medfit.ch, …) don't go through here: App.js lets
 * brandingConfig.js win whenever the hostname matches one.
 *
 * Payload (GET /:locale/v2/branding/:id, 404 when inactive):
 *   { id, name, primary_color, secondary_color, logo_url, favicon_url,
 *     privacy_url, imprint_url, therapist_url, portal_url }
 */
import { Capacitor } from "@capacitor/core";
import http from "@shared/api/http";
import { applyPwaLinks, setFavicon } from "@shared/branding/pwa";

const TENANT_KEY = "tenantId";
const CACHE_KEY = "tenantBranding";
const URL_PARAM = "m";
// Links from mails: <portal>/m/<id>/rest-of-path (see captureTenantFromUrl).
const PATH_PREFIX = /^\/m\/(\d+)(?=\/|$)/;

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Private mode / storage disabled: the brand just won't survive a reload.
  }
};

export function getTenantId() {
  return readStorage(TENANT_KEY);
}

export function clearTenant() {
  writeStorage(TENANT_KEY, null);
  writeStorage(CACHE_KEY, null);
}

/**
 * Store a branding payload (from the endpoint or a login response) as the
 * current tenant, or forget the tenant when it is null.
 */
export function storeTenantBranding(branding) {
  if (!branding || branding.id == null) {
    clearTenant();
    return;
  }
  writeStorage(TENANT_KEY, String(branding.id));
  writeStorage(CACHE_KEY, JSON.stringify(branding));
}

/** The cached brand of the current tenant, for the first paint. */
export function getCachedTenantBranding() {
  const tenantId = getTenantId();
  if (!tenantId) return null;
  try {
    const cached = JSON.parse(readStorage(CACHE_KEY));
    return cached && String(cached.id) === tenantId ? cached : null;
  } catch {
    return null;
  }
}

/**
 * Take `?m=<id>` off the URL and remember it. Must run before the first
 * render: useToken (App and AuthProvider) rewrites the URL without its query
 * once it finds an auth_token. Only `m` is removed here, so auth_token and
 * auth_data are still there for useToken. `m=0` (or an empty/invalid value)
 * goes back to sophyapp.
 *
 * Mails link to `<portal>/m/<id>/…` instead, because their templates append
 * paths to the app link ("%{app_link}/public-access"); that prefix is read
 * the same way and taken off the path.
 */
export function captureTenantFromUrl() {
  const url = new URL(window.location.href);
  const pathMatch = url.pathname.match(PATH_PREFIX);
  if (!pathMatch && !url.searchParams.has(URL_PARAM)) return getTenantId();

  let value;
  if (pathMatch) {
    value = pathMatch[1];
    url.pathname = url.pathname.slice(pathMatch[0].length) || "/";
  } else {
    value = (url.searchParams.get(URL_PARAM) || "").trim();
  }
  url.searchParams.delete(URL_PARAM);
  window.history.replaceState(
    window.history.state,
    document.title,
    url.pathname + url.search + url.hash
  );

  if (/^[1-9]\d*$/.test(value)) {
    if (value !== getTenantId()) {
      // Another tenant's cache must not paint the first frame.
      writeStorage(CACHE_KEY, null);
    }
    writeStorage(TENANT_KEY, value);
  } else {
    clearTenant();
  }
  return getTenantId();
}

/**
 * Load the tenant's brand from the public endpoint and cache it. Resolves
 * with null when the tenant has no active branding (404), which also forgets
 * the tenant; rejects on other errors so the caller keeps the cached brand.
 */
export async function fetchTenantBranding(id) {
  try {
    const { data } = await http.get(
      `/:locale/v2/branding/${encodeURIComponent(id)}`
    );
    // A login or another ?m= link may have switched tenants meanwhile.
    if (getTenantId() !== String(id)) return getCachedTenantBranding();
    storeTenantBranding(data);
    return data && data.id != null ? data : null;
  } catch (error) {
    if (error?.response?.status === 404) {
      if (getTenantId() === String(id)) clearTenant();
      return null;
    }
    throw error;
  }
}

// --- Look -------------------------------------------------------------------

const parseHex = (color) => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color || "").trim());
  if (!match) return null;
  const hex =
    match[1].length === 3
      ? match[1].split("").map((c) => c + c).join("")
      : match[1];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
};

/**
 * `weight` of `color` mixed into `other`, like color-mix(in srgb, …). Done in
 * JS for hex colours so older WebViews (iOS < 16.2) get the shades too.
 */
const mix = (color, other, weight) => {
  const a = parseHex(color);
  const b = parseHex(other);
  if (!a || !b) {
    return `color-mix(in srgb, ${color} ${Math.round(weight * 100)}%, ${other})`;
  }
  return (
    "#" +
    a
      .map((channel, i) =>
        Math.round(channel * weight + b[i] * (1 - weight))
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
};

/**
 * The theme variables of variables.css, derived from the tenant's colours.
 * The shades follow the sophyapp theme (#84bd00 → #f5f9ea, graph ramp from
 * 40 % tint to 60 % shade).
 */
export function tenantCssVariables(branding) {
  const main = branding.primary_color;
  return {
    "--main-color": main,
    "--main-color-dark": mix(main, "#000000", 0.8),
    "--second-color": branding.secondary_color || mix(main, "#000000", 0.7),
    "--background-active": mix(main, "#ffffff", 0.08),
    "--background-light": mix(main, "#ffffff", 0.6),
    "--color-graph-1": mix(main, "#ffffff", 0.4),
    "--color-graph-2": mix(main, "#ffffff", 0.6),
    "--color-graph-3": mix(main, "#ffffff", 0.8),
    "--color-graph-4": mix(main, "#ffffff", 0.9),
    "--color-graph-5": mix(main, "#000000", 0.9),
    "--color-graph-6": main,
    "--color-graph-7": mix(main, "#000000", 0.6),
  };
}

const CSS_VARIABLES = Object.keys(
  tenantCssVariables({ primary_color: "#000000" })
);

const isIco = (url) => /\.ico(\?|#|$)/i.test(url || "");

/**
 * Home-screen manifest for the tenant as a data: URL. Relative URLs would
 * resolve against the data: URL, so start_url, scope and icons are absolute;
 * start_url carries ?m= so the installed app opens in the tenant's look.
 */
const tenantManifestUrl = (branding) => {
  const origin = window.location.origin;
  const startUrl = `${origin}/?m=${branding.id}`;
  const icons = [branding.favicon_url, branding.logo_url]
    .filter(Boolean)
    .map((src) => ({
      src,
      sizes: "any",
      ...(isIco(src) ? { type: "image/x-icon" } : {}),
    }));
  const manifest = {
    id: startUrl,
    name: branding.name,
    short_name: branding.name,
    start_url: startUrl,
    scope: `${origin}/`,
    display: "standalone",
    theme_color: branding.primary_color,
    background_color: "#ffffff",
    icons,
  };
  return `data:application/manifest+json,${encodeURIComponent(
    JSON.stringify(manifest)
  )}`;
};

/** Colours, title, favicon and home-screen tags of the tenant. */
export function applyTenantBranding(branding) {
  if (!branding || typeof document === "undefined") return;
  const style = document.documentElement.style;
  if (parseHex(branding.primary_color)) {
    Object.entries(tenantCssVariables(branding)).forEach(([name, value]) =>
      style.setProperty(name, value)
    );
  }

  if (branding.name) document.title = branding.name;
  const icon = branding.favicon_url || branding.logo_url;
  if (icon) setFavicon(icon);

  // The store app has no manifest or home-screen install; its hostname is
  // localhost anyway.
  if (Capacitor.isNativePlatform()) return;
  // iOS can't use .ico as touch icon; the logo is the better fallback there.
  const touchIcon =
    branding.favicon_url && !isIco(branding.favicon_url)
      ? branding.favicon_url
      : branding.logo_url;
  applyPwaLinks({
    name: branding.name,
    manifest: tenantManifestUrl(branding),
    appleTouchIcon: touchIcon,
  });
}

/** Remove the tenant's colours again; App.js restores title, icon and tags. */
export function resetTenantBranding() {
  if (typeof document === "undefined") return;
  const style = document.documentElement.style;
  CSS_VARIABLES.forEach((name) => style.removeProperty(name));
}
